/**
 * SpeedmasterLoyalty.Config.js
 *
 * Singleton configuration module for the loyalty/rewards system.
 * Loads speedmaster.* config values once, caches them, and exposes
 * helpers for profile data, price levels, points, and date calculations.
 *
 * Extracted from SpeedmasterCore.Config.js during the Loyalty split.
 */
define('SpeedmasterLoyalty.Config', [
    'SC.Configuration',
    'Profile.Model',
    'underscore'
], function(Configuration, ProfileModel, _) {
    'use strict';

    /* ------------------------------------------------------------------ */
    /*  Private state                                                      */
    /* ------------------------------------------------------------------ */

    var _configCache = null;

    var DEFAULT_PRICE_LEVEL = {
        value: '0',
        name: 'Default',
        label: 'Default',
        upperlimit: 0,
        lowerlimit: 0,
        hexcolor: '#DDD'
    };

    /* ------------------------------------------------------------------ */
    /*  Config loader (lazy, cached)                                       */
    /* ------------------------------------------------------------------ */

    function _loadConfig() {
        if (_configCache) { return _configCache; }

        _configCache = {
            priceLevelMap:  Configuration.get('speedmaster.PriceLevelMap') || [],
            multiplierMap:  Configuration.get('speedmaster.MultiplerMap') || [],
            pointsEndDate:  Configuration.get('speedmaster.EndDate') || ''
        };

        return _configCache;
    }

    /* ------------------------------------------------------------------ */
    /*  Profile helpers                                                    */
    /* ------------------------------------------------------------------ */

    /**
     * Returns a plain object with commonly-needed profile data.
     * Safe to call repeatedly — ProfileModel.getInstance() is a singleton.
     */
    function getProfile() {
        var profile = ProfileModel.getInstance();
        var isLoading = ProfileModel.getPromise().state() !== 'resolved';

        // Fully authenticated — active server session, tier pricing applied.
        var isLoggedIn =
            profile.get('isLoggedIn') === 'T' && profile.get('isGuest') === 'F';

        // Recognized — recognition cookie persists but session expired.
        // Customer sees their name, tier, and points (all cached/real)
        // but Items API returns retail prices (no server session).
        var isRecognized = !isLoggedIn &&
            profile.get('isRecognized') === 'T' &&
            profile.get('name') &&
            profile.get('name').length > 0;

        var fullName = ((isLoggedIn || isRecognized) && profile.get('name')) || '';
        var nameParts = fullName.split(' ');

        // Points and locked tier — read from custom fields.
        var points = 0;
        var isLockedTier = false;
        if (isLoggedIn || isRecognized) {
            var customfields = profile.get('customfields') || [];
            var pointsField = _.find(customfields, function(f) {
                return f && f.name === 'custentity_points';
            });
            if (pointsField && pointsField.value) {
                points = Number(pointsField.value) || 0;
            }
            var lockedField = _.find(customfields, function(f) {
                return f && f.name === 'custentity_lockedtier';
            });
            if (lockedField) {
                isLockedTier = lockedField.value === 'T' || lockedField.value === true;
            }
        }

        return {
            isLoading: isLoading,
            isLoggedIn: isLoggedIn,
            isRecognized: isRecognized,
            isLockedTier: isLockedTier,
            firstName: nameParts[0] || '',
            lastName: nameParts.length > 1 ? nameParts.slice(1).join(' ') : '',
            fullName: fullName,
            points: points,
            priceLevel: profile.get('priceLevel')
        };
    }

    /* ------------------------------------------------------------------ */
    /*  Price-level helpers                                                */
    /* ------------------------------------------------------------------ */

    /**
     * Find the matching price-level object for a given pricelevel value.
     * Returns null if no match found.
     */
    function getCurrentPriceLevel(pricelevel) {
        var cfg = _loadConfig();
        var match = _.find(cfg.priceLevelMap, function(pl) {
            return pl.value == pricelevel;  // intentional loose equality (string/number)
        });
        return match || null;
    }

    /**
     * Get the sorted price-level map (ascending by lowerlimit).
     */
    function getSortedPriceLevelMap() {
        var cfg = _loadConfig();
        return _.sortBy(cfg.priceLevelMap, function(pl) {
            return Number(pl.lowerlimit) || 0;
        });
    }

    /**
     * Find current, next, and upper-next price levels for loyalty dashboard.
     */
    function getPriceLevelTiers(pricelevel) {
        var sorted = getSortedPriceLevelMap();
        var current = null;
        var next = null;
        var upperNext = null;

        for (var i = 0; i < sorted.length; i++) {
            if (sorted[i].value == pricelevel) {
                current = sorted[i];
            }
            if (current && Number(current.lowerlimit) < Number(sorted[i].lowerlimit)) {
                next = sorted[i];
                if (i + 1 < sorted.length) {
                    upperNext = sorted[i + 1];
                }
                break;
            }
        }

        if (!current) {
            current = DEFAULT_PRICE_LEVEL;
        }

        return {
            current: current,
            next: next,
            upperNext: upperNext
        };
    }

    /* ------------------------------------------------------------------ */
    /*  Points / multiplier helpers                                        */
    /* ------------------------------------------------------------------ */

    /**
     * Find a multiplier object by name (e.g. "Single", "Double").
     */
    function getMultiplier(multiplierName) {
        var cfg = _loadConfig();
        return _.find(cfg.multiplierMap, function(m) {
            return m.name === multiplierName;
        }) || null;
    }

    /**
     * Calculate points for a given price and multiplier name.
     * Returns { points: Number, bgcolor: String }.
     */
    function calcPoints(price, multiplierName) {
        var mult = getMultiplier(multiplierName || 'Single');
        if (mult && mult.multiplier) {
            return {
                points: price * mult.multiplier,
                bgcolor: mult.bgcolor || 'transparent'
            };
        }
        return { points: price, bgcolor: 'transparent' };
    }

    /* ------------------------------------------------------------------ */
    /*  Utility helpers                                                    */
    /* ------------------------------------------------------------------ */

    /**
     * Date difference calculator (from Overview.Home.View.Ext).
     * Returns { year, month, week, day }.
     */
    function dateDiff(startingDate, endingDate) {
        if (!startingDate) {
            return { year: 0, month: 0, week: 0, day: 0 };
        }

        var normalised = String(startingDate).replace(/\//g, '-');
        var startDate = new Date(normalised);
        if (isNaN(startDate.getTime())) {
            return { year: 0, month: 0, week: 0, day: 0 };
        }
        startDate = new Date(startDate.toISOString().slice(0, 10));

        if (!endingDate) {
            endingDate = new Date().toISOString().slice(0, 10);
        }

        var endDate = new Date(endingDate);
        if (startDate > endDate) {
            var swap = startDate;
            startDate = endDate;
            endDate = swap;
        }

        var startYear = startDate.getFullYear();
        var february = (startYear % 4 === 0 && startYear % 100 !== 0) || startYear % 400 === 0 ? 29 : 28;
        var daysInMonth = [31, february, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

        var yearDiff = endDate.getFullYear() - startYear;
        var monthDiff = endDate.getMonth() - startDate.getMonth();
        if (monthDiff < 0) {
            yearDiff--;
            monthDiff += 12;
        }
        var dayDiff = endDate.getDate() - startDate.getDate();
        if (dayDiff < 0) {
            if (monthDiff > 0) {
                monthDiff--;
            } else {
                yearDiff--;
                monthDiff = 11;
            }
            dayDiff += daysInMonth[startDate.getMonth()];
        }

        var weekDiff = 0;
        if (dayDiff / 7 > 0) {
            weekDiff = Math.floor(dayDiff / 7);
            dayDiff = dayDiff % 7;
        }

        return { year: yearDiff, month: monthDiff, week: weekDiff, day: dayDiff };
    }

    /**
     * Get currency symbol from session.
     */
    function getCurrencySymbol() {
        try {
            var sessionCurrency = SC.getSessionInfo('currency');
            return sessionCurrency ? sessionCurrency.symbol : '$';
        } catch (e) {
            return '$';
        }
    }

    /* ------------------------------------------------------------------ */
    /*  Public API                                                         */
    /* ------------------------------------------------------------------ */

    return {
        get: _loadConfig,
        getProfile: getProfile,
        getCurrentPriceLevel: getCurrentPriceLevel,
        getSortedPriceLevelMap: getSortedPriceLevelMap,
        getPriceLevelTiers: getPriceLevelTiers,
        getMultiplier: getMultiplier,
        calcPoints: calcPoints,
        dateDiff: dateDiff,
        getCurrencySymbol: getCurrencySymbol,
        DEFAULT_PRICE_LEVEL: DEFAULT_PRICE_LEVEL
    };
});
